import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { reportHandler } from '../redux/handlers';

export const ReceiptPrintPage = React.forwardRef((props, ref) => {
  const { pos, onDataLoaded } = props;
  let { billID } = useParams();
  const [billData, setBillData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const dispatch = useDispatch();

  const fetchBillData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await dispatch(reportHandler.getBillReportById(billID));
      setBillData(data);
      if (onDataLoaded) onDataLoaded();
    } catch (error) {
      console.error('Error fetching bill data:', error);
      setError('Failed to load receipt data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (billID) {
      fetchBillData();
    }
  }, [billID]);

  const formatNumber = value => {
    if (value === undefined || value === null || value === '-') return '-';
    const num = typeof value === 'string' ? parseFloat(value) : value;
    return num.toLocaleString('th-TH', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  };

  const encryptNum = num => {
    if (!num) return '-';
    num = num.replace(/[-,\s]/g, '');
    return `${num.substring(0, 3)}-${num.substring(3, 4)}XX-XXXX`;
  };

  const getVatCalculator = () => {
    if (!billData || !billData.order || !billData.order.branchPosSelling) {
      return '';
    }

    const business = billData.order.branchPosSelling.branchPos?.branch?.business;

    if (business?.settingVatCalculation?.code) {
      const vatCalculator = business.settingVatCalculation.code;
      if (vatCalculator === 'INCLUDED') return '(Vat Included)';
      if (vatCalculator === 'SEPARATE') return '(Vat Excluded)';
    }
    return '';
  };

  const getBillItems = () => {
    if (!billData || !billData.billItems) {
      return [];
    }

    return billData.billItems.map(item => {
      const orderItem = item.orderItem || {};
      const product = orderItem.product || {};
      const options = orderItem.orderProductOption || [];
      const specificOptions = orderItem.orderProductSpecificOption || [];
      const annotations = orderItem.orderProductAnnotation || [];

      return {
        id: item.id,
        quantity: item.quantity,
        name: product.name || '-',
        price: formatNumber(item.price),
        pricePerPiece: formatNumber(
          billData.order.order_type === 'DELIVERY'
            ? (orderItem.product?.branchProduct &&
              orderItem.product?.branchProduct.length > 0 &&
              orderItem.product?.branchProduct[0].data?.price_delivery
                ? orderItem.product?.branchProduct[0].data?.price_delivery
                : orderItem.product?.branchProduct[0].data?.price || orderItem.product?.price_delivery) || 0
            : (orderItem.product?.branchProduct &&
              orderItem.product?.branchProduct.length > 0 &&
              orderItem.product?.branchProduct[0].data?.price
                ? orderItem.product?.branchProduct[0].data?.price
                : orderItem.product?.branchProduct[0].data?.price || orderItem.product?.price) || 0
        ),
        total: item.price,
        options: [
          ...options.map(opt => ({
            name: opt.productOptionItem?.name || '',
            price: parseFloat(opt.qty || '0') * parseFloat(opt.productOptionItem?.price || '0')
          })),
          ...specificOptions.map(opt => ({
            name: opt.productSpecificOptionItem?.name || '',
            price: parseFloat(opt.qty || '0') * parseFloat(opt.productSpecificOptionItem?.price || '0')
          }))
        ],
        annotations: annotations.map(ann => ann.productAnnotationItem?.name || '')
      };
    });
  };

  const getBusinessInfo = () => {
    if (!billData?.order) {
      return {
        name: '-',
        company: '-',
        taxId: '',
        branchName: '-',
        branchCode: '',
        logoUrl: ''
      };
    }

    const order = billData.order;
    const branchPos = order.branchPosSelling?.branchPos || {};
    const branchData = branchPos.branch || {};
    const business = branchData.business || {};
    const logoUrl = business.image_path || '';

    return {
      name: business.company_name || '-',
      company: business.company_name || '-',
      taxId: business.tax_number || '',
      branchName: branchData.branch_name || '-',
      branchCode: branchData.code ? `(${branchData.code})` : '',
      logoUrl: logoUrl
    };
  };

  const getTableDisplay = () => {
    if (!billData?.order) {
      return '-';
    }

    const order = billData.order;
    const orderType = order.order_type;

    if (orderType === 'DINE_IN' && order.branchTable) {
      const zoneName = order.branchTable.branchTableZone?.name || '';
      const tableName = order.branchTable.name || '';
      return `${zoneName}/${tableName}`;
    } else if (orderType === 'TAKEAWAY') {
      return 'กลับบ้าน';
    } else if (orderType === 'DELIVERY') {
      return 'เดลิเวอรี่';
    }

    return '-';
  };

  const getReceiptDetails = () => {
    if (!billData) {
      return {
        date: '-',
        posId: '-',
        receiptNo: '-',
        staffName: '-',
        tableInfo: '-'
      };
    }

    const user = billData.userBusinessGroup || {};
    const order = billData.order || {};

    const dateObj = new Date(billData.created_at || new Date());
    const formattedDate = `${dateObj.getDate().toString().padStart(2, '0')}/${(dateObj.getMonth() + 1)
      .toString()
      .padStart(2, '0')}/${dateObj.getFullYear()} ${dateObj.getHours().toString().padStart(2, '0')}:${dateObj
      .getMinutes()
      .toString()
      .padStart(2, '0')}`;

    const receiptNo = billData.receipt_no || order.code || '-';

    const staffName =
      user.first_name && user.last_name ? `${user.first_name} ${user.last_name}` : user.first_name || '-';

    return {
      date: formattedDate,
      posId: order.branchPosSelling?.branchPos?.pos_id || '-',
      receiptNo: receiptNo,
      staffName: staffName,
      tableInfo: getTableDisplay()
    };
  };

  const getTotals = () => {
    if (!billData) {
      return {
        subtotal: '0.00',
        discount: '0.00',
        pointDiscount: '0.00',
        serviceCharge: '0.00',
        netTotal: '0.00',
        vat: '0.00',
        totalAfterDiscount: '0.00',
        taxableAmount: '0.00',
        lossRounded: '0.00',
        finalAmount: '0.00',
        creditCardFee: '0.00',
        vatOnFee: '0.00'
      };
    }

    return {
      subtotal: formatNumber(billData.total_before_discount || 0),
      discount: formatNumber(-(billData.discount_amount || 0)),
      pointDiscount: formatNumber(-(billData.discount_points || 0)),
      serviceCharge: formatNumber(billData.service_charge || 0),
      netTotal: formatNumber(billData.grand_total || 0),
      vat: formatNumber(billData.vat || 0),
      totalAfterDiscount: formatNumber(billData.total_after_discount || 0),
      taxableAmount: formatNumber(billData.taxable_amount || 0),
      lossRounded: formatNumber(billData.loss_rounded || 0),
      finalAmount: formatNumber(billData.final_profit_loss_rounded || billData.final_amount || 0),
      creditCardFee: formatNumber(billData.credit_card_fee || 0),
      vatOnFee: formatNumber(billData.vat_on_fee || 0)
    };
  };

  const getPaymentDetails = () => {
    if (!billData || !billData.payment || billData.payment.length === 0) {
      return [
        {
          method: 'เงินสด',
          amount: '0.00'
        }
      ];
    }

    return billData.payment.map(payment => {
      return {
        method: payment.paymentMethod?.name || 'ไม่ระบุ',
        amount: formatNumber(payment.price || 0),
        paymentCode: payment.paymentMethod?.code || '',
        providers:
          payment.paymentMethod?.payment_providers &&
          payment.paymentMethod.payment_providers.map(provider => ({
            amount: provider.amount || 0,
            name: provider.payment_provider_name || ''
          }))
      };
    });
  };

  const getMemberInfo = () => {
    const memberData = billData?.member;
    const pointData = billData?.additional_data?.point || {};

    if (!memberData) {
      return {
        id: '-',
        name: '-',
        phone: '-',
        pointsEarned: '0',
        pointsUsed: '0',
        pointsBalance: '0'
      };
    }

    return {
      id: memberData.code || '-',
      name:
        `${memberData.first_name || memberData.first_name_th || ''} ${
          memberData.last_name || memberData.last_name_th || ''
        }`.trim() || '-',
      phone: memberData.phone_number || '-',
      pointsEarned: pointData?.earned ? `+${pointData.earned}` : '0',
      pointsUsed: pointData?.spend || pointData?.used || '0',
      pointsBalance: pointData?.total || pointData?.balance || pointData?.remain || '0'
    };
  };

  const hasCreditCardPayment = () => {
    if (!billData || !billData.payment || billData.payment.length === 0) {
      return false;
    }

    return billData.payment.some(payment => payment.paymentMethod?.code === 'CREDIT_CARD');
  };

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-full">
        <div>กำลังโหลดข้อมูล...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex justify-center items-center h-full">
        <div>{error}</div>
      </div>
    );
  }

  if (!billData) {
    return (
      <div className="flex justify-center items-center h-full">
        <div>ไม่พบข้อมูลใบเสร็จ</div>
      </div>
    );
  }

  const products = getBillItems();
  const business = getBusinessInfo();
  const receipt = getReceiptDetails();
  const totals = getTotals();
  const payments = getPaymentDetails();
  const member = getMemberInfo();
  const isCreditCardPayment = hasCreditCardPayment();

  console.log('payments : ', payments);

  const renderProductOptions = (options, quantity) => {
    if (!options || options.length === 0) return null;

    return options.map((option, index) => (
      <div key={index} className="flex w-full mt-1">
        <div className="w-1/4"></div>
        <div className="w-1/2 text-small text-[0.78vw] print:text-[4.5vw]">-&nbsp;{option.name}</div>
        <div className="w-1/4 text-small text-[0.78vw] print:text-[4.5vw] text-right">
          {Number(option.price || '0') > 0 && '+'}
          {Number(option.price || '0') > 0 && formatNumber((option.price || 0) * quantity || 0)}
        </div>
        <div className="w-1/4 text-small text-[0.78vw] print:text-[4.5vw] text-right"></div>
      </div>
    ));
  };

  const renderProductAnnotations = annotations => {
    if (!annotations || annotations.length === 0) return null;

    return annotations.map((annotation, index) => (
      <div key={index} className="flex w-full mt-1">
        <div className="w-1/4"></div>
        <div className="w-1/2 text-small text-[0.78vw] print:text-[4.5vw]">-&nbsp;{annotation}</div>
        <div className="w-1/4"></div>
        <div className="w-1/4"></div>
      </div>
    ));
  };

  return (
    <div ref={ref} className="flex justify-center text-[0.78vw] print:text-[4.5vw] w-full leading-[1.8]">
      <div
        id="section-to-print"
        className="w-[29.49vw] print:w-full bg-[#FFF] px-[1.70vw] print:px-[3.9vw] py-[1.31vw] print:py-[2.6vw] flex flex-col gap-[0.59vw] print:gap-[1.3vw]"
      >
        <div>
          <div className="text-center w-full flex flex-col items-center">
            {business.logoUrl && (
              <img className="object-cover aspect-square w-[7.71vw] print:w-[25vw] h-fit logo-receipt" src={business.logoUrl} alt="" />
            )}

            <div>
              {business.name} - {business.branchName}
              {business.branchCode}
            </div>

            {business.taxId && <div className="font-large">TAX ID : {business.taxId}</div>}

            <div className="font-semibold mt-[0.51vw]">
              {pos && pos[0] && pos[0].tax_number ? 'ใบกำกับภาษีอย่างย่อ / ใบเสร็จรับเงิน' : 'ใบเสร็จรับเงิน'}
            </div>
          </div>

          <div className="flex flex-col gap-[0.39vw]">
            <div className="flex justify-between gap-[0.98vw]">
              <div className="whitespace-nowrap">วันที่ : {receipt.date}</div>
              <div className="text-right">POS# : {receipt.posId}</div>
            </div>
            <div className="flex justify-between gap-[0.98vw]">
              <div className="text-right">พนักงานขาย : {receipt.staffName}</div>
              <div className="text-right">โต๊ะ : {receipt.tableInfo}</div>
            </div>
            <div className="flex justify-between gap-[0.98vw]">
              <div className="whitespace-nowrap">เลขที่ : {receipt.receiptNo}</div>
            </div>
          </div>

          <div className="flex flex-col gap-[0.39vw]">
            <div className="mt-[0.78vw]">
              <div className="border-dashed border-t-[0.04vw] border-b-[0.04vw] border-black py-[0.39vw] flex">
                <div className="w-1/4 text-center font-medium">จำนวน</div>
                <div className="w-1/2 text-center font-medium">รายการ</div>
                <div className="w-1/4 text-right font-medium">หน่วยละ</div>
                <div className="w-1/4 text-right font-medium">รวมเงิน</div>
              </div>

              <div className="pt-[0.78vw] pb-[0.29vw] font-bold">
                จำนวนสินค้า : {products.reduce((sum, product) => sum + (parseInt(product.quantity) || 0), 0)}
              </div>

              {products.map((product, index) => (
                <div key={product.id || index} className="mb-3">
                  <div className="flex pt-[0.29vw]">
                    <div className="w-1/4 text-center align-top leading-[1.45]">{product.quantity}</div>
                    <div className="w-1/2 align-top break-words leading-[1.45]">{product.name}</div>
                    <div className="w-1/4 text-right align-top leading-[1.45]">{product.pricePerPiece || 0}</div>
                    <div className="w-1/4 text-right align-top leading-[1.45]">
                      {formatNumber(Number(product.total || 0))}
                    </div>
                  </div>

                  {renderProductOptions(product.options, product.quantity)}
                  {renderProductAnnotations(product.annotations)}
                </div>
              ))}
            </div>

            <div className="border-dashed border-t-[0.04vw] border-black h-0 my-[1.28vw] my-sp-13"></div>

            <div className="flex justify-between w-full">
              <div className="whitespace-nowrap">ยอดรวมก่อนส่วนลด</div>
              <div className="text-left">{totals.subtotal}</div>
            </div>

            <div className="flex justify-between w-full">
              <div className="whitespace-nowrap">ส่วนลดท้ายบิล</div>
              <div className="text-left">{totals.discount}</div>
            </div>

            <div className="flex justify-between w-full">
              <div className="whitespace-nowrap">ยอดขายหลังหักส่วนลด</div>
              <div className="text-left">{totals.totalAfterDiscount}</div>
            </div>

            <div className="flex justify-between w-full">
              <div className="whitespace-nowrap">
                Service Charge
                {billData?.service_charge_percent ? ` (${billData.service_charge_percent}%)` : ''}
              </div>
              <div className="text-left">{totals.serviceCharge}</div>
            </div>

            <div className="flex justify-between w-full">
              <div className="whitespace-nowrap">
                {Number(totals.vat) > 0 ? 'มูลค่าสินค้ามีภาษี' : 'มูลค่าสินค้าไม่รวมภาษี'}
              </div>
              <div className="text-left">{totals.taxableAmount}</div>
            </div>

            <div className="flex justify-between w-full">
              <div className="whitespace-nowrap">ภาษีมูลค่าเพิ่ม</div>
              <div className="text-left">{totals.vat}</div>
            </div>

            <div className="flex justify-between w-full text-large font-bold">
              <div className="whitespace-nowrap">ราคาสุทธิ</div>
              <div className="text-left">{totals.netTotal}</div>
            </div>

            <div className="flex justify-between w-full">
              <div className="whitespace-nowrap">จำนวนเงินปัดเศษ</div>
              <div className="text-left">{totals.lossRounded}</div>
            </div>

            <div className="flex justify-between w-full text-large font-bold">
              <div className="whitespace-nowrap">จำนวนที่ต้องชำระ</div>
              <div className="text-left">{totals.finalAmount}</div>
            </div>
            <div className="border-dashed border-t-[0.04vw] border-black h-0 my-[1.28vw] my-sp-13"></div>
            <div className="text-center">{getVatCalculator()}</div>
            <div className="">ช่องทางการชำระเงิน</div>
            {payments.map((payment, index) => (
              <div key={index} className="flex flex-col gap-[0.98vw]">
                <div className="flex justify-between">
                  <div className="whitespace-nowrap">{payment.method}</div>
                  <div className="text-right">
                    {Array.isArray(payment.providers) && payment.providers.length > 0 ? '' : payment.amount}
                  </div>
                </div>
                {Array.isArray(payment.providers) &&
                  payment.providers.map((provider, providerIndex) => (
                    <div className="flex justify-between ml-2" key={`provider-${providerIndex}`}>
                      <div className="whitespace-nowrap pl-[1.5vw]">- {provider.name}</div>
                      <div className="text-right">{formatNumber(provider.amount)}</div>
                    </div>
                  ))}
              </div>
            ))}

            {isCreditCardPayment && (
              <>
                <div className="flex justify-between">
                  <div className="whitespace-nowrap">
                    ค่าธรรมเนียมบัตรเครดิต{' '}
                    {billData.credit_card_fee_percent ? `(${billData.credit_card_fee_percent}%)` : ''}
                  </div>
                  <div className="text-right">{formatNumber(totals.creditCardFee)}</div>
                </div>
                <div className="flex justify-between">
                  <div className="whitespace-nowrap">ภาษีมูลค่าเพิ่ม</div>
                  <div className="text-right">{formatNumber(totals.vatOnFee)}</div>
                </div>
              </>
            )}

            {billData?.additional_data?.cash_out && parseFloat(billData.additional_data.cash_out) > 0 ? (
              <div className="flex justify-between gap-[0.98vw]">
                <div className="whitespace-nowrap">เงินทอน</div>
                <div className="text-right">{formatNumber(billData.additional_data.cash_out)}</div>
              </div>
            ) : (
              ''
            )}

            {billData?.member ? (
              <>
                <div className="border-dashed border-t-[0.04vw] border-black h-0 mt-[1.28vw]"></div>

                <div className="text-center">Member : {member.id}</div>
                <div className="flex justify-between gap-[0.98vw] mt-[1.66vw]">
                  <div className="whitespace-nowrap">คุณ : {member.name}</div>
                  <div className="text-right">เบอร์ : {encryptNum(member.phone)}</div>
                </div>

                <div className="flex mt-[0.78vw]">
                  <div className="w-1/4"></div>
                  <div className="w-1/4 text-right">ได้รับ</div>
                  <div className="w-1/4 text-right">ใช้ไป</div>
                  <div className="w-1/4 text-right">คงเหลือ</div>
                </div>

                <div className="flex">
                  <div className="w-1/4">คะแนน</div>
                  <div className="w-1/4 text-right">{member.pointsEarned}</div>
                  <div className="w-1/4 text-right">{member.pointsUsed}</div>
                  <div className="w-1/4 text-right">{member.pointsBalance}</div>
                </div>

                <div className="border-dashed border-t-[0.04vw] border-black h-0 mt-[1.28vw]"></div>
              </>
            ) : (
              <div className="border-dashed border-t-[0.04vw] border-black h-0 mt-[1.28vw]"></div>
            )}

            <div className="text-center">THANK YOU</div>
            <div className="text-center">ขอบคุณที่ใช้บริการ</div>
            <div className="text-center" style={{ fontSize: '44px' }}>
              {billData?.order && billData?.order.remark ? `(${billData?.order.remark})` : ''}{' '}
            </div>

            <div className="py-7 print:hidden"></div>
          </div>
        </div>
      </div>
    </div>
  );
});

export default ReceiptPrintPage;
